import { useState, useEffect } from 'react';
import { useMutation, gql, useApolloClient } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { UPDATE_RESERVATION } from '../../../graphql/mutations';
import { 
  GET_JOBS, 
  GET_JOBS_FOR_ASSIGNMENTS, 
  GET_JOB_FOR_EDIT, 
  GET_NEXT_RESERVATION, 
  GET_PREVIOUS_RESERVATION 
} from '../../../graphql/queries';
import { RESERVATION_VIEWS, DEFAULT_TIMES, CHECKOUT_TIME_MIN, CHECKOUT_TIME_MAX } from '../constants/editJobConstants';
import { 
  getCurrentReservationData, 
  parseOverrideDateTime, 
  buildReservationUpdateInput 
} from '../utils/editJobUtils';

export const useReservationEdit = (job, previousReservation, nextReservation, onUpdate, onClose, currentReservationWithContact) => {
  const client = useApolloClient();
  
  // State management
  const [reservationView, setReservationView] = useState(RESERVATION_VIEWS.CURRENT);
  const [editingField, setEditingField] = useState(null);
  const [reservationError, setReservationError] = useState('');
  const [reservationEditData, setReservationEditData] = useState({
    checkInDate: '',
    checkInTime: '',
    checkOutDate: '',
    checkOutTime: ''
  });

  // Enhanced cache invalidation for reservation updates
  const invalidateJobComputedFields = () => {
    try {

      
      // 🎯 CRITICAL: Force recalculation of job computed fields (isEarlyCheckin, isLateCheckout)
      // When reservation times change, these GraphQL resolver fields need to be recalculated
      
      // 1. Evict the specific job to force resolver fields to recalculate
      if (job?.id) {
        const jobCacheId = client.cache.identify({ __typename: 'Job', id: job.id });

        client.cache.evict({ id: jobCacheId });
        
        // Also evict the job's embedded reservation data
        if (job?.reservation?.id) {
          const reservationCacheId = client.cache.identify({ __typename: 'Reservation', id: job.reservation.id });

          client.cache.evict({ id: reservationCacheId });
        }
        

      }
      
      // 2. Evict all job-related queries to ensure fresh data

      client.cache.evict({ fieldName: 'jobs' });
      client.cache.evict({ fieldName: 'jobsForAssignments' });
      client.cache.evict({ fieldName: 'readyForInspectionJobs' });
      client.cache.evict({ fieldName: 'completedJobs' });
      client.cache.evict({ fieldName: 'deletedJobs' });
      
      // 🎯 CRITICAL: Clear MySchedule cache to fix reservation update refresh issue
      client.cache.evict({ fieldName: 'myScheduleJobs' });
      
      // 🎯 CRITICAL: Clear optimized assigned tasks caches when reservation times change
      // This ensures the optimized assignments view updates with new early check-in/late checkout indicators
      client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
      client.cache.evict({ fieldName: 'employeeAssignedJobs' });
      
      // 3. Evict reservation-related fields that affect job calculations

      client.cache.evict({ fieldName: 'previousReservationForProperty' });
      client.cache.evict({ fieldName: 'nextReservationForProperty' });
      client.cache.evict({ fieldName: 'propertyReservations' });
      
      // 4. Force garbage collection

      client.cache.gc();
      
      // 5. Force refetch of all affected queries (safer than store reset)
      // 5. Force refetch of all affected queries (safer than store reset)
      setTimeout(() => {
        // Force refetch the main queries rather than reset entire store
        client.refetchQueries({
          include: ['GetJobs', 'GetJobsForAssignments', 'GetJob', 'GetNextReservation', 'GetPreviousReservation']
        }).catch(error => {
          console.warn('Refetch warning (non-critical):', error);
        });
      }, 100);
      

    } catch (error) {
              console.error('Error invalidating cache:', error);
    }
  };

  // Mutations
  const [updateReservation, { loading: updateReservationLoading }] = useMutation(UPDATE_RESERVATION, {
    // 🎯 CRITICAL: Force refetch of job queries that embed reservation data
    refetchQueries: [
      GET_JOBS,
      GET_JOBS_FOR_ASSIGNMENTS, 
      GET_JOB_FOR_EDIT,
      GET_NEXT_RESERVATION,
      GET_PREVIOUS_RESERVATION
    ],
    onCompleted: (data) => {

      
      setEditingField(null);
      toast.success(`${reservationView === 'previous' ? 'Previous r' : 'R'}eservation updated successfully!`);
      
      // 🎯 ENHANCED: Force job computed fields recalculation
      invalidateJobComputedFields();
      
      // Close modal and refresh UI with forced network refresh
      if (data?.updateReservation) {

        if (onUpdate) onUpdate(true); // Force network refresh for computed fields
        setTimeout(() => onClose(), 100);
      }
    },
    onError: (error) => {
      console.error('Error updating reservation:', error);
      setReservationError('Error updating reservation: ' + error.message);
    }
  });

  const [updatePreviousReservation, { loading: updatePreviousReservationLoading }] = useMutation(UPDATE_RESERVATION, {
    // 🎯 CRITICAL: Force refetch of job queries that embed reservation data
    refetchQueries: [
      GET_JOBS,
      GET_JOBS_FOR_ASSIGNMENTS, 
      GET_JOB_FOR_EDIT,
      GET_NEXT_RESERVATION,
      GET_PREVIOUS_RESERVATION
    ],
    onCompleted: (data) => {

      
      setEditingField(null);
      toast.success('Previous reservation updated successfully!');
      
      // 🎯 ENHANCED: Force job computed fields recalculation
      invalidateJobComputedFields();
      
      // Close modal and refresh UI with forced network refresh
      if (data?.updateReservation) {

        if (onUpdate) onUpdate(true); // Force network refresh for computed fields
        setTimeout(() => onClose(), 100);
      }
    },
    onError: (error) => {
      console.error('Error updating previous reservation:', error);
      setReservationError('Error updating previous reservation: ' + error.message);
    }
  });

  // Get current reservation data
  const baseReservationData = getCurrentReservationData(
    reservationView, 
    job, 
    previousReservation, 
    nextReservation
  );

  // Enhance current reservation with contact info if needed
  const currentReservationData = reservationView === RESERVATION_VIEWS.CURRENT && currentReservationWithContact
    ? {
        ...baseReservationData,
        email: baseReservationData?.email || currentReservationWithContact?.email,
        mobile_phone: baseReservationData?.mobile_phone || currentReservationWithContact?.mobile_phone
      }
    : baseReservationData;

  const snapToHour = (time) => {
    if (!time) return time;
    const hour = time.split(':')[0];
    return `${hour}:00`;
  };

  // Handle edit field initialization
  const handleEditField = (field) => {
    if (!currentReservationData) return;
    
    const isCheckIn = field === 'checkin';
    const currentDate = isCheckIn ? currentReservationData.check_in : currentReservationData.check_out;
    const currentTime = isCheckIn ? currentReservationData.check_in_time : currentReservationData.check_out_time;
    const overrideDate = isCheckIn ? currentReservationData.override_check_in : currentReservationData.override_check_out;
    
    let dateValue = '';
    let timeValue = '';
    
    if (overrideDate) {
      const parsed = parseOverrideDateTime(overrideDate, isCheckIn ? DEFAULT_TIMES.CHECK_IN : DEFAULT_TIMES.CHECK_OUT);
      dateValue = parsed.date;
      timeValue = snapToHour(parsed.time);
    } else {
      dateValue = currentDate ? format(new Date(currentDate), 'yyyy-MM-dd') : '';
      timeValue = snapToHour(currentTime || (isCheckIn ? DEFAULT_TIMES.CHECK_IN : DEFAULT_TIMES.CHECK_OUT));
    }
    
    setReservationEditData({
      checkInDate: isCheckIn ? dateValue : reservationEditData.checkInDate,
      checkInTime: isCheckIn ? timeValue : reservationEditData.checkInTime,
      checkOutDate: !isCheckIn ? dateValue : reservationEditData.checkOutDate,
      checkOutTime: !isCheckIn ? timeValue : reservationEditData.checkOutTime
    });
    
    setEditingField(field);
  };

  // Handle save edit
  const handleSaveEdit = async (field) => {
    if (!currentReservationData) return;
    
    try {
      const isCheckIn = field === 'checkin';
      const dateValue = isCheckIn ? reservationEditData.checkInDate : reservationEditData.checkOutDate;
      const timeValue = isCheckIn ? reservationEditData.checkInTime : reservationEditData.checkOutTime;
      
      if (!dateValue || !timeValue) {
        setReservationError('Please provide both date and time');
        return;
      }
      
      if (!isCheckIn && (timeValue < CHECKOUT_TIME_MIN || timeValue > CHECKOUT_TIME_MAX)) {
        setReservationError('Check-out time must be between 10:00 AM and 4:00 PM to avoid shifting the reservation day');
        return;
      }
      
      const updateInput = buildReservationUpdateInput(
        currentReservationData.id,
        field,
        dateValue,
        timeValue,
        currentReservationData
      );
      
      const mutation = reservationView === 'previous' ? updatePreviousReservation : updateReservation;
      
      await mutation({
        variables: { input: updateInput }
      });
      
    } catch (error) {
      console.error('Error updating reservation:', error);
      setReservationError('Error updating reservation: ' + error.message);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingField(null);
    setReservationEditData({
      checkInDate: '',
      checkInTime: '',
      checkOutDate: '',
      checkOutTime: ''
    });
  };

  // Reset reservation view when switching tabs
  const resetReservationState = () => {
    setReservationView(RESERVATION_VIEWS.CURRENT);
    setEditingField(null);
    setReservationError('');
  };

  return {
    // State
    reservationView,
    setReservationView,
    editingField,
    reservationError,
    setReservationError,
    reservationEditData,
    setReservationEditData,
    currentReservationData,
    
    // Loading states
    updateReservationLoading: updateReservationLoading || updatePreviousReservationLoading,
    
    // Handlers
    handleEditField,
    handleSaveEdit,
    handleCancelEdit,
    resetReservationState
  };
};